import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import Paper from '@mui/material/Paper';
import {
  Box,
  Button,
  Checkbox,
  CardActionArea,
  FormControl,
  Grid,
  InputLabel,
  ListItemText,
  FormControlLabel,
  Switch,
  styled,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Typography,
  createTheme,
  useMediaQuery,
} from '@mui/material';
import './style.css';
import LeadStore from '../../store/LeadStore';
import Card from '@mui/material/Card';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormLabel from '@mui/material/FormLabel';
import CardMedia from '@mui/material/CardMedia';
import { isEmpty } from '../../utils/helper';
import elevation1 from '../../assets/images/elevation-style/1.jpeg';
import elevation2 from '../../assets/images/elevation-style/2.jpeg';
import elevation3 from '../../assets/images/elevation-style/3.jpeg';
import elevation4 from '../../assets/images/elevation-style/4.jpeg';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export const EditConstructionSpecification = observer(({ leadId, details, handleEditDetails }) => {
  const theme = createTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const CustomFormControlLabel = styled(FormControlLabel)({
    '& .MuiFormControlLabel-label': {
      fontSize: '0.75rem',
      color: '#0009',
    },
  });

  const [formData, setFormData] = useState({
    id: details?.clientConstructionSpecificationsId ?? null,
    leadId,
    masonry: details?.masonry,
    masonryOther: details?.masonryOther,
    rainWaterHarvesting: details?.rainWaterHarvesting,
    windows: details?.windows,
    elevationStyle: details?.elevationStyle ?? '',
    additionalDetails: details?.additionalDetails,
    createdBy: details?.CreatedBy,
    createdDate: details?.createdDate,
    updatedBy: details?.updatedBy,
    updatedDate: details?.updatedDate,
  });

  const [errors, setErrors] = useState({
    masonry: false,
    masonryOther: false,
    rainWaterHarvesting: false,
    windows: false,
    elevationStyle: false,
    additionalDetails: false,
  });

  const [errorMessages, setErrorMessages] = useState({
    masonry: '',
    masonryOther: '',
    rainWaterHarvesting: '',
    windows: '',
    elevationStyle: '',
    additionalDetails: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'rainWaterHarvesting') {
      setFormData({ ...formData, [name]: e.target.checked });
    } else {
      setFormData({ ...formData, [name]: value });
    }

    // Perform validation
    setErrors({ ...errors, [name]: isEmpty(value) });
  };

  const handleUpdateClientConstructionSpecificationsSubmit = (e) => {
    e.preventDefault();

    const updatedErrors = {
      masonryOther: formData.masonry === 'Other' && isEmpty(formData.masonryOther),
    };
    setErrors(updatedErrors);

    if (!Object.values(updatedErrors).some((error) => error === true)) {
      const payload = { ...formData };
      isEmpty(formData.id)
        ? LeadStore.createClientConstructionSpecifications(payload)
        : LeadStore.updateClientConstructionSpecifications(payload);
    }
  };

  useEffect(() => {
    LeadStore.updateClientConstructionSpecificationsStatus && handleEditDetails();
  }, [LeadStore.updateClientConstructionSpecificationsStatus]);

  const isContactNumberError = (value, name) => {
    if (isEmpty(value)) {
      return true;
    } else if (!/^\d{0,10}$/.test(value)) {
      setErrorMessages({
        ...errors,
        [name]: 'Please enter valid 10 digit number',
      });
      return true;
    } else {
      return false;
    }
  };

  const isNumberError = (value, name) => {
    if (isEmpty(value)) {
      return false;
    } else if (!/^\d{0,10}$/.test(value)) {
      setErrorMessages({
        ...errors,
        [name]: 'Please enter valid 10 digit number',
      });
      return true;
    } else {
      return false;
    }
  };

  const masonryList = [
    { id: 1, name: '6" block' },
    { id: 2, name: '9" red block' },
    { id: 3, name: 'Other' },
  ];

  const windowsList = [
    { id: 1, name: 'Upvc' },
    { id: 2, name: 'Hardwood' },
    { id: 3, name: 'Alluminium (Recomonded for Commercial)' },
  ];

  return (
    <Paper sx={{ px: '30px', py: '20px', pb: '40px' }}>
      <Box
        id="client-info-form"
        component="form"
        noValidate
        autoComplete="off"
        onSubmit={(event) => {
          event.preventDefault();
          handleUpdateClientConstructionSpecificationsSubmit(event);
        }}
      >
        <Stack spacing={4}>
          <Stack spacing={3}>
            <Grid rowGap={2} container>
              <Grid item xs={12} sm={6} md={6} lg={6} sx={{ pr: isSmallScreen ? '0px' : '10px' }}>
                <Stack direction={'column'} spacing={3}>
                  <FormControl variant="standard" error={Boolean(errors.masonry)}>
                    <InputLabel id="masonryLabel" shrink={true}>
                      Masonry
                    </InputLabel>
                    <Select
                      labelId="masonryLabel"
                      id="masonry"
                      name="masonry"
                      value={formData.masonry ?? ''}
                      onChange={(e) => {
                        const {
                          target: { value },
                        } = e;
                        setFormData({
                          ...formData,
                          ['masonry']: value,
                          ['masonryOther']: value !== 'Other' ? null : formData.masonryOther,
                        });
                        setErrors({ ...errors, ['masonry']: isEmpty(value) });
                      }}
                      label="Masonry"
                      MenuProps={MenuProps}
                    >
                      {masonryList.map((site) => (
                        <MenuItem key={site.id} value={site.name}>
                          <ListItemText primary={site.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {formData.masonry === 'Other' && (
                    <TextField
                      label="Masonry Other"
                      id="masonryOther"
                      size="small"
                      variant="standard"
                      required
                      name="masonryOther"
                      multiline
                      rows={1}
                      value={formData.masonryOther ?? ''}
                      onChange={(e) => handleChange(e)}
                      error={Boolean(errors.masonryOther)}
                      sx={{ fontSize: '12px' }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  )}
                  <CustomFormControlLabel
                    control={
                      <Switch
                        checked={formData.rainWaterHarvesting}
                        size="medium"
                        onChange={handleChange}
                        name="rainWaterHarvesting"
                      />
                    }
                    label="Rain Water Harvesting"
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={6} sx={{ pl: isSmallScreen ? '0px' : '10px' }}>
                <Stack direction={'column'} spacing={3}>
                  <FormControl variant="standard" error={Boolean(errors.windows)}>
                    <InputLabel id="windowsLabel" shrink={true}>
                      Windows
                    </InputLabel>
                    <Select
                      labelId="windowsLabel"
                      id="windows"
                      name="windows"
                      value={formData.windows ?? ''}
                      onChange={(e) => {
                        const {
                          target: { value },
                        } = e;
                        setFormData({ ...formData, ['windows']: value });
                        setErrors({ ...errors, ['windows']: isEmpty(value) });
                      }}
                      label="Windows"
                      MenuProps={MenuProps}
                    >
                      {windowsList.map((site) => (
                        <MenuItem key={site.id} value={site.name}>
                          <ListItemText primary={site.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              </Grid>
            </Grid>
          </Stack>
          <Stack spacing={3}>
            <Stack direction={'column'} spacing={2} sx={{ color: '#626A72', fontWeight: '500' }}>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: '700',
                  alignSelf: 'start',
                }}
                component="p"
              >
                Elevation Style
              </Typography>
            </Stack>
            <Stack flexDirection={'row'} rowGap={3} columnGap={4} flexWrap={'wrap'}>
              <FormControl>
                <RadioGroup
                  aria-labelledby="elevation-style-group-label"
                  name="elevationStyle"
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    columnGap: '3',
                    rowGap: '',
                  }}
                  value={formData.elevationStyle}
                  onChange={handleChange}
                >
                  <FormControlLabel
                    control={<Radio />}
                    value={1}
                    label={
                      <Card sx={{ maxWidth: 345 }}>
                        <CardActionArea>
                          <CardMedia
                            component="img"
                            height="140"
                            image={elevation1}
                            alt="green iguana"
                            onClick={() => window.open(elevation1, '_blank')}
                          />
                        </CardActionArea>
                      </Card>
                    }
                  />
                  <FormControlLabel
                    control={<Radio />}
                    value={2}
                    label={
                      <Card sx={{ maxWidth: 345 }}>
                        <CardActionArea>
                          <CardMedia
                            component="img"
                            height="140"
                            image={elevation2}
                            alt="green iguana"
                            onClick={() => window.open(elevation2, '_blank')}
                          />
                        </CardActionArea>
                      </Card>
                    }
                  />
                  <FormControlLabel
                    control={<Radio />}
                    value={3}
                    label={
                      <Card sx={{ maxWidth: 345 }}>
                        <CardActionArea>
                          <CardMedia
                            component="img"
                            height="140"
                            image={elevation3}
                            alt="green iguana"
                            onClick={() => window.open(elevation3, '_blank')}
                          />
                        </CardActionArea>
                      </Card>
                    }
                  />
                  <FormControlLabel
                    control={<Radio />}
                    value={4}
                    label={
                      <Card sx={{ maxWidth: 345 }}>
                        <CardActionArea>
                          <CardMedia
                            component="img"
                            height="140"
                            image={elevation4}
                            alt="green iguana"
                            onClick={() => window.open(elevation4, '_blank')}
                          />
                        </CardActionArea>
                      </Card>
                    }
                  />
                </RadioGroup>
              </FormControl>
            </Stack>
          </Stack>
          <Stack direction={'row'} sx={{ ml: 'auto' }} alignSelf={isSmallScreen ? '' : 'end'} spacing={2}>
            <Button variant="outlined" color="inherit" fullWidth size="small" onClick={() => handleEditDetails(false)}>
              Cancel
            </Button>
            <Button variant="contained" type="submit" fullWidth color="primary" size="small">
              Update
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Paper>
  );
});
